import React, { useState, useEffect, useMemo } from 'react';
import { colors, API, authHeaders } from '../utils/theme';

// Classificação de lançamento — compartilhada pela Conciliação de fatura e
// pelos Lançamentos (04/11/2026, extraída da ConciliacaoPage).

// Empresas para vincular lançamentos/conciliações (chamado #31). Vazio = sem empresa.
// TK Milhas/XMTech24/Radar Voo adicionadas em 02/09/2026 — o server valida a
// mesma lista (EMPRESAS_VALIDAS em routes/conciliacao.js); mudou lá, muda aqui.
export const EMPRESAS = ['Ivoei', 'XMiles', 'Franchising', 'TK Milhas', 'XMTech24', 'Radar Voo'];

// Operações (centros de fechamento) de sistema. Os CADASTRADOS pelo gestor
// vêm de GET /centros-fechamento e entram depois destes (useOperacoes).
export const OPERACOES_SISTEMA = [
  { v: 'OPERACAO_X', label: 'Operação X' },
  { v: 'OPERACAO_J', label: 'Operação J' },
  // De sistema desde 07/10/2026 — a regra aponta pra eles.
  { v: 'OPERACAO_AGENCIA', label: 'Operação Agência' },
  { v: 'TECNOLOGIA_XMTECH24', label: 'Tecnologia · XMTech24' },
  { v: 'COMPRAS_X', label: 'Compras X' },
  { v: 'COMPRAS_J', label: 'Compras J' },
  { v: 'TAXA_EMBARQUE_X', label: 'Taxa embarque X' },
  { v: 'TAXA_EMBARQUE_J', label: 'Taxa embarque J' },
  { v: 'PESSOAL_X', label: 'Pessoal X' },
  { v: 'PESSOAL_J', label: 'Pessoal J' },
];

// Operações de sistema + as cadastradas e ativas; o rótulo cobre também as
// desativadas (linha antiga segue com o nome).
export function useOperacoes() {
  const [cad, setCad] = useState([]);
  useEffect(() => {
    let ok = true;
    fetch(`${API}/centros-fechamento?todos=1`, { headers: authHeaders() }).then(r => r.json())
      .then(d => { if (ok && Array.isArray(d)) setCad(d.filter(c => !c.sistema)); })
      .catch(() => {});
    return () => { ok = false; };
  }, []);
  return useMemo(() => {
    const lista = [...OPERACOES_SISTEMA, ...cad.filter(c => c.ativo).map(c => ({ v: c.codigo, label: c.rotulo }))];
    const rotulos = {
      PESSOAL: 'Pessoal', EXCLUIDO: 'Excluído',
      ...Object.fromEntries(OPERACOES_SISTEMA.map(o => [o.v, o.label])),
      ...Object.fromEntries(cad.map(c => [c.codigo, c.rotulo])),
    };
    return { lista, rotulo: (v) => rotulos[v] || v };
  }, [cad]);
}

// Categorias LOCAIS do Finance que o plano de contas não conhece (02/09/2026): o
// desconto de antecipação é classificado como Bonus/Antecipação por aqui (a
// regra de boot tagueia as linhas da fatura com exatamente estas strings).
const CATEGORIAS_LOCAIS = [
  { group: 'Bonus', name: 'Antecipação' },
];
// Categorias do lançamento (group = categoria, name = subcategoria).
// Compartilhado pelo modal de lançamento e pelo avulso em lote. Cada entrada
// carrega `local: true` quando foi criada no Finance (sistema=0) — essas são
// bloqueadas pra empresa XMiles, cuja categoria nasce no TechMiles (DRE) — e
// `xm` com o id do plano de contas quando a categoria VEM do plano.
//
// Fonte primária: o plano de contas do TechMiles, espelhado em categorias_custo
// (18/09/2026). Era o Manager, que caiu quando as credenciais saíram do .env —
// o 500 sumia no .catch e o dropdown despencou pra uma categoria só (Bonus).
// O Manager segue como FALLBACK, depois do plano: se um dia responder de novo,
// o dedupe abaixo descarta o que repetir e quem chegou primeiro (o plano) vence.
export function useTiposDespesa() {
  const [plano, setPlano] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [locais, setLocais] = useState([]);
  useEffect(() => {
    let ok = true;
    fetch(`${API}/plano-contas/subcategorias`, { headers: authHeaders() }).then(r => r.json())
      .then(d => { if (ok && Array.isArray(d)) setPlano(d); })
      .catch(() => {});
    fetch(`${API}/xmiles/expense-types`).then(r => r.json())
      .then(d => { if (ok) setTipos(Array.isArray(d) ? d : (Array.isArray(d?.data) ? d.data : [])); })
      .catch(() => {});
    fetch(`${API}/conciliacao/categorias-finance`, { headers: authHeaders() }).then(r => r.json())
      .then(d => { if (ok && Array.isArray(d)) setLocais(d); })
      .catch(() => {});
    return () => { ok = false; };
  }, []);
  return useMemo(() => {
    const base = [
      ...plano.map(s => ({ group: s.grupo, name: s.nome, local: false, xm: s.xm_categoria_id })),
      ...tipos.map(t => ({ group: t.group, name: t.name, local: false, xm: null })),
      ...CATEGORIAS_LOCAIS.map(c => ({ ...c, local: false, xm: null })),
      ...locais.map(c => ({ group: c.grupo, name: c.subcategoria, local: !Number(c.sistema), xm: null })),
    ];
    const vistos = new Set();
    return base.filter(e => {
      const k = `${(e.group || '').toUpperCase()}|${(e.name || '').toUpperCase()}`;
      if (vistos.has(k)) return false;
      vistos.add(k);
      return true;
    });
  }, [plano, tipos, locais]);
}

// Selects de categoria/subcategoria com criação de categoria local (03/09/2026).
// Regra do gestor: categoria da XMiles nasce no TECHMILES (o DRE de lá é quem
// consome) — criar categoria aqui só vale pra outras empresas. Com XMiles
// selecionada, as locais somem do dropdown e o "+ Nova categoria" não aparece.
// O server valida a mesma regra (categoriaVetadaParaXMiles).
export function CamposCategoria({ categoria, subcategoria, empresa, onChange, inp, sel, lbl }) {
  const tipos = useTiposDespesa();
  const [extras, setExtras] = useState([]); // criadas nesta sessão do modal
  const [criando, setCriando] = useState(false);
  const [novo, setNovo] = useState({ grupo: '', sub: '' });
  const [criandoBusy, setCriandoBusy] = useState(false);
  const bloqueiaLocais = empresa === 'XMiles';
  const todos = [...tipos, ...extras];
  const visiveis = bloqueiaLocais ? todos.filter(t => !t.local) : todos;
  const grupos = [...new Set(visiveis.map(e => e.group).filter(Boolean))].sort();
  // Carrega o `xm` junto: é ao escolher a SUBcategoria que o vínculo com o
  // plano de contas fica definido (grupo sozinho não classifica lançamento).
  const subs = visiveis.filter(e => e.group === categoria && e.name)
    .sort((a, b) => a.name.localeCompare(b.name));

  // Trocou a empresa pra XMiles com uma categoria local selecionada? Limpa —
  // o server recusaria de qualquer jeito.
  useEffect(() => {
    if (bloqueiaLocais && categoria && todos.length
        && !todos.some(t => t.group === categoria && !t.local)) {
      onChange({ categoria: '', subcategoria: '', xm_categoria_id: null });
    }
  }, [bloqueiaLocais]); // eslint-disable-line

  const criar = async () => {
    const grupo = novo.grupo.trim();
    if (!grupo || criandoBusy) return;
    setCriandoBusy(true);
    try {
      const r = await fetch(`${API}/conciliacao/categorias-finance`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ grupo, subcategoria: novo.sub.trim() || null }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setExtras(x => [...x, { group: grupo, name: novo.sub.trim() || null, local: true }]);
      onChange({ categoria: grupo, subcategoria: novo.sub.trim() || '', xm_categoria_id: null });
      setCriando(false); setNovo({ grupo: '', sub: '' });
    } catch (e) { alert('Criar categoria: ' + e.message); }
    setCriandoBusy(false);
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={lbl}>Categoria (grupo de despesa)</label>
          <select style={sel} value={categoria}
            onChange={e => {
              if (e.target.value === '__nova__') { setCriando(true); return; }
              onChange({ categoria: e.target.value, subcategoria: '', xm_categoria_id: null });
            }}>
            <option value="">{grupos.length ? 'Selecione...' : 'Carregando...'}</option>
            {/* No TOPO de propósito: no fim de 30 categorias ninguém achava. */}
            {!bloqueiaLocais && <option value="__nova__">＋ Nova categoria…</option>}
            {grupos.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Subcategoria (despesa)</label>
          <select style={sel} value={subcategoria} disabled={!categoria}
            onChange={e => {
              const alvo = subs.find(x => x.name === e.target.value);
              onChange({ subcategoria: e.target.value, xm_categoria_id: alvo?.xm ?? null });
            }}>
            <option value="">{!categoria ? 'Escolha a categoria primeiro' : 'Selecione...'}</option>
            {subs.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
          </select>
        </div>
      </div>
      {bloqueiaLocais && (
        <div style={{ fontSize: 10, color: colors.textDim, marginTop: 4 }}>
          Empresa XMiles: só categorias do TechMiles — o DRE nasce lá. Categoria nova aqui é só pra outras empresas.
        </div>
      )}
      {criando && !bloqueiaLocais && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input style={{ ...inp, width: 170 }} placeholder="Nova categoria" value={novo.grupo}
            onChange={e => setNovo(n => ({ ...n, grupo: e.target.value }))} />
          <input style={{ ...inp, width: 170 }} placeholder="Subcategoria (opcional)" value={novo.sub}
            onChange={e => setNovo(n => ({ ...n, sub: e.target.value }))} />
          <button onClick={criar} disabled={!novo.grupo.trim() || criandoBusy}
            style={{
              padding: '6px 12px', borderRadius: 8, border: 'none', background: colors.primary,
              color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}>
            {criandoBusy ? 'Criando...' : 'Criar'}
          </button>
          <button onClick={() => { setCriando(false); setNovo({ grupo: '', sub: '' }); }}
            style={{
              padding: '6px 10px', borderRadius: 8, border: `1px solid ${colors.border}`,
              background: 'transparent', color: colors.textMuted, cursor: 'pointer', fontSize: 12,
            }}>Cancelar</button>
        </div>
      )}
    </div>
  );
}
